import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ZorroTableComponent } from '../../common/custom-table/zorro-table.component';
import { CustomTableColumn } from '../../../core/models/custom-table-fields';
import { ProcessLogEntry } from '../../common/process-logs-loader/process-log-entry';
import { ProcessLogsLoaderComponent } from '../../common/process-logs-loader/process-logs-loader.component';

export interface ScriptValidationTreeNode {
    id: string;
    displayName: string;
    count?: number;
    validated?: boolean;
    expanded?: boolean;
    enabled?: boolean;
    children?: ScriptValidationTreeNode[];
}

export interface ScriptValidationItem {
    id: string;
    name: string;
    url?: string;
    validated: boolean;
    enabled: boolean;
    expanded: boolean;
    fields?: { name: string; dataType: string; validated?: boolean }[];
}

@Component({
    selector: 'app-script-validation',
    standalone: true,
    imports: [CommonModule, FormsModule, ZorroTableComponent, ProcessLogsLoaderComponent],
    templateUrl: './script-validation.component.html',
    styleUrl: './script-validation.component.scss',
})
export class ScriptValidationComponent implements OnInit {
    activeModal = inject(NgbActiveModal);

    get validatedCount(): number {
        const seen = new Set<string>();
        let count = 0;
        Object.values(this.nodeDataMap).forEach(items => {
            items.forEach(item => {
                if (!seen.has(item.id)) {
                    seen.add(item.id);
                    if (item.enabled) count++;
                }
            });
        });
        return count;
    }

    get totalCount(): number {
        const seen = new Set<string>();
        Object.values(this.nodeDataMap).forEach(items => {
            items.forEach(item => seen.add(item.id));
        });
        return seen.size;
    }
    // ScriptValidationTreeNode
    @Input() treeNodes: any[] = [];

    @Input() items: ScriptValidationItem[] = [];

    @Input() nodeDataMap: { [nodeId: string]: ScriptValidationItem[] } = {};

    @Input() columnHeaders: { name: string; dataType: string } = { name: 'Name', dataType: 'Data Type' };

    @Input() type: 'web' | 'api' | 'mobile' | 'user_flow' = 'api';

    /** When true, shows the reusable process-logs + animated gradient loader. */
    @Input() isProcessing = false;

    /**
     * Process logs for the loader view.
     * Pass undefined to hide the timeline; pass [] (or entries) to show it.
     */
    @Input() processLogs: ProcessLogEntry[] | null | undefined;

    @Input() processStatusLabel = 'Thinking...';

    @Input() processIsActive = true;

    @Input() processIsError = false;

    /** Shown above the loader when processIsError is true (outside process-logs-loader). */
    @Input() processErrorMessage = '';

    @Input() title = 'Load & Validate Recorded Activities';

    @Output() nodeSelected = new EventEmitter<ScriptValidationTreeNode>();

    expandedGroups = new Set<string>();
    selectedTreeNode: ScriptValidationTreeNode | null = null;
    nodeContents: ScriptValidationItem[] = [];
    tableColumns: CustomTableColumn[] = [];

    ngOnInit() {
        this.tableColumns = this.getTableColumnConfigs(this.type);
        this.nodeContents = this.items;
        this.treeNodes?.forEach(group => {
            group.children?.forEach((child: ScriptValidationTreeNode) => {
                if (child.validated === undefined) child.validated = false;
                if (child.expanded === undefined) child.expanded = false;
                if (child.enabled === undefined) child.enabled = false;
            });
        });
        if (this.treeNodes?.length) {
            this.expandedGroups.add(this.treeNodes[0].id);
            const firstChild = this.treeNodes[0].children?.[0];
            if (firstChild) {
                this.selectNode(firstChild);
            }
        }
    }

    toggleGroup(node: ScriptValidationTreeNode): void {
        if (this.expandedGroups.has(node.id)) {
            this.expandedGroups.delete(node.id);
        } else {
            this.expandedGroups.add(node.id);
        }
    }

    isExpanded(node: ScriptValidationTreeNode): boolean {
        return this.expandedGroups.has(node.id);
    }

    selectNode(node: ScriptValidationTreeNode): void {
        this.selectedTreeNode = node;
        this.nodeContents = this.nodeDataMap[node.id] ?? [];
        this.nodeContents.forEach(i => (i.expanded = false));
        const match = this.nodeContents.find(i => i.name === node.displayName);
        if (match) {
            match.expanded = true;
        } else if (this.nodeContents.length) {
            this.nodeContents[0].expanded = true;
        }
        this.nodeSelected.emit(node);
    }

    toggleExpansion(item: ScriptValidationItem): void {
        const isExpanding = !item.expanded;
        this.nodeContents.forEach(i => (i.expanded = false));
        item.expanded = isExpanding;
        if (isExpanding) {
            for (const group of this.treeNodes) {
                const matchingNode = group.children?.find(
                    (child: ScriptValidationTreeNode) => child.displayName === item.name
                );
                if (matchingNode) {
                    this.selectedTreeNode = matchingNode;
                    this.expandedGroups.add(group.id);
                    this.nodeSelected.emit(matchingNode);
                    break;
                }
            }
        }
    }

    formatCount(count: number): string {
        return count.toString().padStart(2, '0');
    }

    getTableColumnConfigs(type: string): any[] {
        const headerStyle = {
            fontSize: '12px',
            background: '#7593F2',
            padding: '6px 10px !important',
            color: '#FFFFFF',
        };

        switch (type) {
            case 'web':
                return [
                    {
                        key: 'name',
                        display_name: 'Object Name',
                        headerStyles: headerStyle,
                        cellStyles: { background: '#DED6FF' },
                    },
                    {
                        key: 'locator',
                        display_name: 'Locators',
                        headerStyles: headerStyle,
                        _config: {
                        type: 'image_with_text',
                        prefixImage: {
                            targetKey: 'type',
                            values: {
                            'Mozilla': 'assets/images/virtusa-icons/firefox.png',
                            'Chrome': 'assets/images/virtusa-icons/chrome.png',
                            'Edge': 'assets/images/virtusa-icons/edge.png'
                            }
                        }
                        },
                    },
                ];
            case 'api':
                return [
                    { key: 'name', display_name: 'Name', headerStyles: headerStyle },
                    { key: 'dataType', display_name: 'Data Type', headerStyles: headerStyle },
                ];
            case 'mobile':
                return [
                    { key: 'name', display_name: 'Element Name', headerStyles: headerStyle },
                    { key: 'dataType', display_name: 'Locator', headerStyles: headerStyle },
                ];
            case 'user_flow':
                return [
                    { key: 'name', display_name: 'Step Name', headerStyles: headerStyle },
                    { key: 'dataType', display_name: 'Type', headerStyles: headerStyle },
                ];
            default:
                return [
                    { key: 'name', display_name: this.columnHeaders.name, headerStyles: headerStyle },
                    { key: 'dataType', display_name: this.columnHeaders.dataType, headerStyles: headerStyle },
                ];
        }
    }
}
